import React from 'react';
import PropTypes from 'prop-types';
import api from '../../api';

/**
 * TagFilter component (single select version)
 * Previously rendered multiple toggle buttons allowing multi-select.
 * Requirement change: now a single dropdown (select) – only one tag can be active.
 * We continue to accept/return `activeTags` as an array for backwards compatibility,
 * but we only ever keep 0 or 1 items in that array.
 */
const TagFilter = ({
    activeTags = [],
    setActiveTags,
    setSearchLoading,
    setSearchResults,
    setSearchPageInfo,
    setSearchError,
    resetSearchState,
    productsScrollRef,
    suppressInternalClear = false,
}) => {
    const tagOptions = ['red', 'blue', 'yellow', 'black', 'grey', 'green'];

    const runSearch = async (tags) => {
        if (!tags || tags.length === 0) return; // Nothing to search
        setSearchLoading(true);
        setSearchError(null);
        try {
            const result = await api.searchProductsByTags(tags, { mode: 'AND', first: 50 });
            setSearchResults(result.products);
            setSearchPageInfo(result.pageInfo);
            if (productsScrollRef?.current) productsScrollRef.current.scrollTop = 0;
        } catch (err) {
            console.error(err);
            setSearchError('Error filtering by tags.');
        } finally {
            setSearchLoading(false);
        }
    };

    const handleChange = async (e) => {
        const value = e.target.value;
        const next = value ? [value] : [];
        setActiveTags(next);
        resetSearchState();
        if (next.length === 0) {
            if (productsScrollRef?.current) productsScrollRef.current.scrollTop = 0;
            return;
        }
        await runSearch(next);
    };

    const clearSelection = () => {
        setActiveTags([]);
        resetSearchState();
        if (productsScrollRef?.current) productsScrollRef.current.scrollTop = 0;
    };

    const selectedValue = activeTags[0] || '';

    return (
        <div className="tag-filter tag-filter--singleSelect">
            <label className="tag-filter__label" htmlFor="tag-filter-select">
                Filter by colour
            </label>
            <div className="tag-filter__controls">
                <select
                    id="tag-filter-select"
                    className="tag-filter__select"
                    value={selectedValue}
                    onChange={handleChange}
                >
                    <option value="">All colours</option>
                    {tagOptions.map((tag) => (
                        <option key={tag} value={tag}>
                            {tag}
                        </option>
                    ))}
                </select>
                {!suppressInternalClear && activeTags.length > 0 && (
                    <button
                        type="button"
                        onClick={clearSelection}
                        className="tag-filter__clear button btn span"
                        aria-label="Clear colour filter"
                    >
                        Back
                    </button>
                )}
            </div>
        </div>
    );
};

TagFilter.propTypes = {
    activeTags: PropTypes.array,
    setActiveTags: PropTypes.func,
    setSearchLoading: PropTypes.func.isRequired,
    setSearchResults: PropTypes.func.isRequired,
    setSearchPageInfo: PropTypes.func.isRequired,
    setSearchError: PropTypes.func.isRequired,
    resetSearchState: PropTypes.func.isRequired,
    productsScrollRef: PropTypes.shape({ current: PropTypes.any }),
    suppressInternalClear: PropTypes.bool,
};

export default TagFilter;
